import { useEffect, useState, useRef } from "react";
import { api, fmt, mediaUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, ImagePlus, Quote, ArrowRight, ShieldCheck } from "lucide-react";

export default function Testimonials() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [state, setState] = useState(null);
  const [body, setBody] = useState(""); const [headline, setHeadline] = useState("");
  const [imageUrl, setImageUrl] = useState(""); const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    api.get("/public/testimonials").then((r) => setItems(r.data.testimonials));
    api.get("/public/state").then((r) => setState(r.data)).catch(() => {});
    document.title = "NOWCOMMAND — Wins from the realm";
  }, []);
  const onPickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setUploading(true);
    try {
      const data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      const resp = await api.post("/upload/image", { data, purpose: "testimonial" });
      setImageUrl(resp.data.url); toast.success("Image attached");
    } catch (e) { console.error("Testimonial image upload failed", e); toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const submit = async (e) => { e.preventDefault();
    if (!body.trim()) return;
    try { await api.post("/testimonials", { headline, body, image_url: imageUrl }); toast.success("Submitted — Robin will review."); setBody(""); setHeadline(""); setImageUrl(""); }
    catch (e) { console.error("Testimonial submission failed", e); toast.error("Submit failed"); }
  };
  const fullUrl = mediaUrl;
  const seated = state?.active_members ?? 0;
  const isMember = user?.tier === "full" || user?.tier === "foundational" || user?.role === "admin";

  return (
    <div className="min-h-screen">
      {/* HERO — proof is the strongest argument */}
      <section className="px-4 sm:px-6 lg:px-10 py-20 border-b border-borderGold">
        <div className="max-w-5xl mx-auto text-center">
          <div className="overline mb-4">// PROOF, NOT PROMISES</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05]">
            What happens when you stop<br className="hidden sm:block"/> bowing to money and the clock.
          </h1>
          <p className="text-textMuted mt-5 max-w-2xl mx-auto leading-relaxed">
            These aren't paid actors or borrowed quotes. They're stewards who took a seat at the table and watched their relationship with money and time change. Read theirs. Then write your own.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-mono text-textMuted">
            <span className="text-gold">{seated} stewards seated</span>
            <span>·</span>
            <span>{items.length} testimonies and counting</span>
            <span>·</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-gold"/>Cancel anytime</span>
          </div>
          {!isMember && (
            <Link to="/pricing" data-testid="testimonials-hero-cta" className="btn-gold mt-8 inline-flex">Take your seat at the table <ArrowRight className="w-4 h-4"/></Link>
          )}
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-10 py-16">
        <div className="max-w-5xl mx-auto">
          {/* TESTIMONIES */}
          <div className="grid sm:grid-cols-2 gap-px bg-borderGold">
            {items.map((t) => (
              <article key={t.id} data-testid={`testimony-${t.id}`} className="bg-void p-8 relative">
                {t.status === "featured" && <div className="absolute top-3 right-3 overline text-gold"><Sparkles className="w-3 h-3 inline"/> FEATURED</div>}
                <Quote className="w-7 h-7 text-gold/30 mb-3" />
                {t.headline && <h3 className="font-display text-2xl text-gold mb-3 leading-tight">"{t.headline}"</h3>}
                {t.image_url && <img src={fullUrl(t.image_url)} alt="" className="w-full max-h-72 object-cover border border-borderGold mb-4" />}
                <p className="text-cream/95 whitespace-pre-wrap leading-relaxed">{t.body}</p>
                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-borderGold">
                  <div className="w-10 h-10 border border-borderGold bg-surface overflow-hidden">
                    {t.user_avatar ? <img src={t.user_avatar} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center font-display text-gold">{(t.user_name||"?")[0]}</div>}
                  </div>
                  <div>
                    <div className="text-cream font-medium">{t.user_name}</div>
                    <div className="overline">{fmt.date(t.created_at)}</div>
                  </div>
                </div>
              </article>
            ))}
            {items.length === 0 && <div className="bg-void p-10 col-span-full text-textMuted text-center">The first testimonies are being gathered. Yours could be the one that opens the door for someone else.</div>}
          </div>

          {/* MID-PAGE CONVERSION + ETHOS */}
          {!isMember && (
            <div className="mt-16 panel p-8 md:p-12 text-center" data-testid="testimonials-mid-cta">
              <div className="overline mb-3">// YOUR NAME BELONGS HERE</div>
              <h2 className="font-display text-3xl sm:text-4xl text-cream leading-tight">Every story above started with one decision.</h2>
              <p className="text-textMuted mt-4 max-w-2xl mx-auto">Not a perfect moment. Not more money first. Just a yes — in the appointed time. Chronos says "wait." Mammon says "you can't afford it." Both are lying, and the people above prove it.</p>
              <Link to="/pricing" className="btn-gold mt-7 inline-flex">Cross the threshold <ArrowRight className="w-4 h-4"/></Link>
              <p className="text-textDim text-xs font-mono mt-3 uppercase tracking-[0.2em]">Auto-renew · Cancel, pause or downgrade anytime · You stay sovereign</p>
            </div>
          )}

          {/* SUBMISSION (members) */}
          {isMember ? (
            <form onSubmit={submit} className="mt-16 panel p-6 md:p-8" data-testid="testimony-form">
              <div className="overline mb-2">// SHARE YOUR WIN</div>
              <h3 className="font-display text-3xl text-cream">Tell us what shifted.</h3>
              <p className="text-textMuted mt-2">Robin reviews submissions before publishing. With your permission, your testimony may appear on this page and on Robin's socials — your words could be the proof that frees someone else.</p>
              <input data-testid="testimony-headline" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Headline (optional, e.g. 'My first month back in Kairos')" className="mt-5"/>
              <textarea data-testid="testimony-body" rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What changed since you stepped into NOWCOMMAND?" className="mt-3"/>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="testimony-image-input" />
                <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost text-xs" data-testid="testimony-image-btn">
                  <ImagePlus className="w-4 h-4"/> {uploading ? "Uploading…" : imageUrl ? "Change image" : "Attach a win screenshot (optional)"}
                </button>
                {imageUrl && <img src={fullUrl(imageUrl)} alt="" className="w-16 h-16 object-cover border border-borderGold"/>}
                {imageUrl && <button type="button" onClick={() => setImageUrl("")} className="overline text-ruby">Remove</button>}
              </div>
              <button data-testid="testimony-submit" type="submit" className="btn-gold mt-4"><Send className="w-4 h-4"/>Submit testimony</button>
            </form>
          ) : (
            <div className="mt-16 text-center">
              <Link to="/pricing" className="btn-gold">Cross the threshold to share your story</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
